import { Badge } from '@design-system';
import { OnboardingLayout, useSlideSetup, useOverviewSlide } from '@/features/client-onboarding';
import { OnboardingResultsAreIn } from '@/pages/OnboardingResultsAreIn';
import { IncompleteIntakeAlert } from '@/shared/components/incomplete-intake-alert';
import { selectSession, selectFeatureFlags } from '@/model';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FullScreenLoadingOverlay } from '@/components/FullScreenLoadingOverlay';
import { useConsumerResumePath } from '@/features/consumer';
import { possessive, SessionType } from '@shared';

import { ClipboardList, SlidersHorizontal, PiggyBank, Compass } from 'lucide-react';

import { motion } from 'framer-motion';

interface Step {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  isOptional?: boolean;
}

const DEFAULT_STEPS: Step[] = [
  {
    number: 1,
    icon: <ClipboardList className="text-muted-foreground size-6" />,
    title: 'Review Your Results',
    description:
      'Understand your predicted care needs, personalized using over 500 million datapoints from people like you.',
  },
  {
    number: 2,
    icon: <SlidersHorizontal className="text-muted-foreground size-6" />,
    title: 'Personalize Your Plan',
    description:
      'Refine your plan with your place of care and anticipated family caregiving support at each phase.',
  },
  {
    number: 3,
    icon: <PiggyBank className="text-muted-foreground size-6" />,
    title: 'Evaluate Your Funding Solutions',
    description: 'Explore multiple recommended ways to protect you and fund your LTC needs.',
  },
];

const D2C_STEPS: Step[] = [
  {
    number: 1,
    icon: <ClipboardList className="text-muted-foreground size-6" />,
    title: 'Review Your Results',
    description:
      'Understand your predicted care needs, personalized using over 500 million datapoints from people like you.',
  },
  {
    number: 2,
    icon: <SlidersHorizontal className="text-muted-foreground size-6" />,
    title: 'Personalize Your Plan',
    description:
      'Refine your plan with your place of care and anticipated family caregiving support at each phase.',
  },
  {
    number: 3,
    icon: <Compass className="text-muted-foreground size-6" />,
    title: 'Build Your Funding Plan',
    description:
      'Discover how your assets, income, or insurance can work together to cover the costs of your LTC plan.',
  },
];

// A d2c consumer already in the guided flow goes straight back to where they left off.
export function OnboardingOverview({ clientId }: ClientIdProps) {
  const { isChecking, resumePath } = useConsumerResumePath(clientId);

  if (isChecking) return <FullScreenLoadingOverlay isLoading />;
  if (resumePath) return <Navigate to={resumePath} replace />;
  return <OverviewSlide />;
}

function OverviewSlide() {
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const STEPS = isD2C ? D2C_STEPS : DEFAULT_STEPS;

  const { clientFirstName, visibleIndices, visibleLineIndices } = useOverviewSlide(STEPS.length);

  useSlideSetup({
    slidePage: OnboardingOverview,
    nextButtonLabel: "Let's Get Started",
    nextSlidePage: OnboardingResultsAreIn,
    hideProgressStepper: true,
  });

  return (
    <OnboardingLayout>
      <div className="flex flex-col items-center justify-center py-6 md:py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-2 text-center"
        >
          <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-4xl">
            Building {possessive(clientFirstName)}{' '}
            <span className="text-primary">Long-Term Care Plan</span>
          </h1>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-foreground text-balance text-center text-base md:text-lg"
        >
          Understand your needs, personalize your care, and choose the best funding options
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-6 w-full max-w-2xl"
        >
          <IncompleteIntakeAlert />
        </motion.div>
        <div className="relative mt-12 w-full max-w-2xl md:mt-16">
          {STEPS.map((step, index) => {
            const isLastStep = index === STEPS.length - 1;
            const isStepVisible = visibleIndices.includes(index);

            return (
              <div
                key={step.number}
                className="relative flex"
              >
                <div className="relative mr-4 flex flex-col items-center md:mr-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isStepVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                    transition={{
                      duration: 0.2,
                      type: 'spring',
                      stiffness: 250,
                      damping: 15,
                    }}
                    className="relative"
                  >
                    <div className="border-border bg-background flex size-12 items-center justify-center rounded-xl border-2 md:size-14">
                      {step.icon}
                    </div>
                    <div className="bg-primary text-primary-foreground border-background absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full border-2 text-xs font-semibold">
                      {step.number}
                    </div>
                  </motion.div>

                  {!isLastStep && (
                    <motion.div
                      initial={{ scaleY: 0 }}
                      animate={visibleLineIndices.includes(index) ? { scaleY: 1 } : { scaleY: 0 }}
                      transition={{ duration: 0.1, ease: 'easeOut' }}
                      className="bg-border min-h-20 w-0.5 flex-1 origin-top md:min-h-16"
                    />
                  )}
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={isStepVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                  className="flex-1"
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-950 md:text-lg">
                      {step.title}
                    </h3>
                    {step.isOptional && <Badge variant="secondary">Optional</Badge>}
                  </div>
                  <p className="text-foreground mt-1 text-sm leading-relaxed md:text-base">
                    {step.description}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </OnboardingLayout>
  );
}
